import {
  formatSlashCommandDraft,
  listFleetSlashCommands,
  type FleetSlashCommand,
} from "./fleet-slash-commands.ts";

/** Composer-only slash actions. Not sent to the gateway. */
export const COMPOSER_LOCAL_SLASH_COMMANDS: readonly FleetSlashCommand[] = [
  {
    name: "code",
    description: "Insert a fenced code block",
    acceptsArgs: false,
  },
];

/**
 * Gateway slash commands plus local composer actions for the `/` picker.
 */
export function listComposerSlashCommands(): FleetSlashCommand[] {
  return [...COMPOSER_LOCAL_SLASH_COMMANDS, ...listFleetSlashCommands()];
}

export function isComposerLocalSlashCommand(name: string): boolean {
  return COMPOSER_LOCAL_SLASH_COMMANDS.some((command) => command.name === name);
}

/**
 * Apply a local slash action. Returns the next draft, or null to let the
 * gateway-bound slash token stand.
 */
export function applyComposerLocalSlashCommand(
  command: FleetSlashCommand,
  draft: string,
): string | null {
  if (command.name !== "code") {
    return null;
  }
  const token = formatSlashCommandDraft(command).trim();
  const trimmed = draft.trim();
  const base = trimmed === token || /^\/[A-Za-z0-9._-]*$/.test(trimmed) ? "" : draft;
  const gap = base.length === 0 || base.endsWith("\n") ? "" : "\n";
  return `${base}${gap}\`\`\`\n\n\`\`\`\n`;
}

/** Intercept Send when the draft is only a local slash token. */
export function applyComposerLocalSlashFromDraft(draft: string): string | null {
  const trimmed = draft.trim();
  const match = /^\/([A-Za-z0-9._-]+)$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const name = match[1] ?? "";
  const command = COMPOSER_LOCAL_SLASH_COMMANDS.find((entry) => entry.name === name);
  if (!command) {
    return null;
  }
  return applyComposerLocalSlashCommand(command, draft);
}

/**
 * True when picking a slash command from the picker should immediately send
 * the command, rather than just inserting the formatted draft.
 *
 * Rules (JAISIU-3094):
 *   - Args-accepting commands insert a trailing space which closes the picker;
 *     the user adds args and presses Enter to send. No auto-send.
 *   - No-args gateway commands are complete on pick: Enter must also send.
 *   - Local commands (e.g. /code) expand the draft to multi-line content; that
 *     expansion must not be followed by a send.
 */
export function shouldSendAfterSlashPick(command: FleetSlashCommand): boolean {
  if (command.acceptsArgs) {
    return false;
  }
  if (isComposerLocalSlashCommand(command.name)) {
    return false;
  }
  return true;
}
